import { useState, useCallback } from 'react';
import apiService from '../../../services/ApiService';

export const useUsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [oauthFilter, setOauthFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const pageSize = 10;

  const loadUsers = useCallback(async (page = currentPage, search = searchTerm) => {
    try {
      setLoading(true);
      const params = {
        page: page - 1,
        size: pageSize,
        search: search || undefined,
      };
      
      const response = await apiService.getAdminUsers(params);
      setUsers(response.users || []);
      setTotalPages(response.totalPages || 1);
      setTotalItems(response.totalItems || 0);
    } catch (error) {
      console.error('Failed to load users:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  const updateUserStatus = useCallback(async (userId, isActive, onMetricsUpdate) => {
    const newIsActive = !isActive;
    
    // Optimistic update
    setUsers((prev) =>
      prev.map((user) =>
        user.userId === userId ? { ...user, isActive: newIsActive } : user
      )
    );

    // Update metrics
    if (onMetricsUpdate) {
      onMetricsUpdate((prevMetrics) => ({
        ...prevMetrics,
        activeUsers: (prevMetrics.activeUsers || 0) + (newIsActive ? 1 : -1),
        inactiveUsers: (prevMetrics.inactiveUsers || 0) + (newIsActive ? -1 : 1),
      }));
    }

    try {
      if (isActive) {
        await apiService.deactivateUser(userId);
      } else {
        await apiService.activateUser(userId);
      }
    } catch (error) {
      // Revert on error
      setUsers((prev) =>
        prev.map((user) =>
          user.userId === userId ? { ...user, isActive } : user
        )
      );
      
      if (onMetricsUpdate) {
        onMetricsUpdate((prevMetrics) => ({
          ...prevMetrics,
          activeUsers: (prevMetrics.activeUsers || 0) + (isActive ? 1 : -1),
          inactiveUsers: (prevMetrics.inactiveUsers || 0) + (isActive ? -1 : 1),
        }));
      }
      
      throw error;
    }
  }, []);

  const updateUserRole = useCallback(async (userId, role, shouldAdd, currentUser, onMetricsUpdate) => {
    const currentRoles = currentUser?.roles || '';
    const rolesArray = currentRoles
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean);

    let newRoles;
    if (shouldAdd && !rolesArray.includes(role)) {
      newRoles = [...rolesArray, role].join(',');
    } else if (!shouldAdd && rolesArray.includes(role)) {
      newRoles = rolesArray.filter((r) => r !== role).join(',');
    } else {
      return { success: true, roles: currentRoles };
    }

    // Optimistic update
    setUsers((prev) =>
      prev.map((user) =>
        user.userId === userId ? { ...user, roles: newRoles } : user
      )
    );

    // Update metrics
    if (role === 'ADMIN' && onMetricsUpdate) {
      onMetricsUpdate((prevMetrics) => ({
        ...prevMetrics,
        adminCount: (prevMetrics.adminCount || 0) + (shouldAdd ? 1 : -1),
      }));
    }

    try {
      if (shouldAdd) {
        await apiService.addRoleToUser(userId, role);
      } else {
        await apiService.removeRoleFromUser(userId, role);
      }
      
      return { success: true, roles: newRoles };
    } catch (error) {
      // Revert on error
      setUsers((prev) =>
        prev.map((user) =>
          user.userId === userId ? { ...user, roles: currentRoles } : user
        )
      );
      
      if (role === 'ADMIN' && onMetricsUpdate) {
        onMetricsUpdate((prevMetrics) => ({
          ...prevMetrics,
          adminCount: (prevMetrics.adminCount || 0) + (shouldAdd ? -1 : 1),
        }));
      }
      
      throw error;
    }
  }, []);

  const getFilteredUsers = useCallback(() => {
    return users.filter((user) => {
      // OAuth filter
      const providers = user.oauth2Providers?.map(
        (p) => p.providerId || p.provider_id
      ) || [];
      const matchesOAuth =
        oauthFilter === 'all' || providers.includes(oauthFilter);

      // Role filter
      const matchesRole =
        roleFilter === 'all' || user.roles?.includes(roleFilter);

      return matchesOAuth && matchesRole;
    });
  }, [users, oauthFilter, roleFilter]);

  const handleSearch = useCallback((search) => {
    setSearchTerm(search);
    setCurrentPage(1);
    loadUsers(1, search);
  }, [loadUsers]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    loadUsers(page, searchTerm);
  }, [loadUsers, searchTerm]);

  return {
    users,
    loading,
    currentPage,
    totalPages,
    totalItems,
    searchTerm,
    oauthFilter,
    roleFilter,
    setSearchTerm,
    setOauthFilter,
    setRoleFilter,
    loadUsers,
    updateUserStatus,
    updateUserRole,
    getFilteredUsers,
    handleSearch,
    handlePageChange,
  };
};
